import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
} from 'react';
import * as ReactVirtualized from 'react-virtualized';

import {
  LegacyBox,
  Divider,
  LegacyText,
  IconButton,
  ActionBar,
  Typography,
  Grid,
  CloseIcon,
  FilterListIcon,
} from '@gogame/components';
import { useHover } from '@gogame/hooks-ts';
import { DateValueFormatter, DateRangeFormatter } from '@gogame/intl-ts';

import AnnotationListItem from './AnnotationListItem';

const ROW_HEIGHT = 48;

export const AnnotationList = ({
  items = [],
  onHover,
  ids = [],
  date,
  period,
  onCleanSelection,
  onOpenFilters,
}) => {
  const [ref, hovered] = useHover();
  const scrollToIndex = useMemo(() => {
    return ids.length ? undefined : 0;
  }, []);

  const [scrolling, setScrolling] = useState(false);

  const filteredAnnotations = useMemo(() => {
    if (!ids.length) {
      return items;
    }

    return items.filter(({ id }) => ids.includes(id));
  }, [items, ids]);

  const timeoutRef = useRef(null);

  const rowRenderer = useCallback(
    ({ index, style }) => {
      const annotation = filteredAnnotations[index];
      return (
        <AnnotationListItem
          key={index}
          {...annotation}
          style={style}
          ref={ref}
        />
      );
    },
    [filteredAnnotations]
  );

  const handleScroll = () => {
    clearTimeout(timeoutRef.current);
    setScrolling(true);
    timeoutRef.current = setTimeout(() => {
      setScrolling(false);
    }, 200);
  };

  useEffect(() => {
    if (!scrolling) {
      onHover(hovered);
    }
  }, [hovered, scrolling]);

  return (
    <LegacyBox grow={1}>
      <Divider orientation={'vertical'} flexItem />
      <LegacyBox grow={1} vertical={true}>
        {ids.length ? (
          <ActionBar
            title={
              <Grid direction={'column'}>
                <Grid item>Annotations ({ids.length})</Grid>
                <Grid item>
                  <Typography variant={'caption'}>
                    <DateValueFormatter
                      value={date}
                      template={'dd MMM, yyyy'}
                      timeZone={'local'}
                    />
                  </Typography>
                </Grid>
              </Grid>
            }
            primaryActions={
              <IconButton
                icon={<CloseIcon />}
                tooltip={'Close'}
                tooltipPlacement={'left'}
                onClick={onCleanSelection}
              />
            }
          />
        ) : (
          <ActionBar
            title={
              <Grid direction={'column'}>
                <Grid item>Annotations ({items.length})</Grid>
                <Grid item>
                  <Typography variant={'caption'}>
                    <DateRangeFormatter
                      value={period}
                      template={'dd MMM, yyyy'}
                    />
                  </Typography>
                </Grid>
              </Grid>
            }
            primaryActions={
              <IconButton
                icon={<FilterListIcon />}
                onClick={onOpenFilters}
                tooltip={'Annotations filter'}
                tooltipPlacement={'left'}
              />
            }
          />
        )}
        <Divider />
        {filteredAnnotations && filteredAnnotations.length ? (
          <ReactVirtualized.List
            scrollToIndex={scrollToIndex}
            style={{ outline: 'none', padding: '8px 0' }}
            width={460}
            height={219}
            onScroll={handleScroll}
            rowCount={filteredAnnotations.length}
            rowHeight={ROW_HEIGHT}
            rowRenderer={rowRenderer}
          />
        ) : (
          <LegacyBox
            vertical={true}
            grow={'1'}
            align={'center'}
            justifyContent={'center'}
          >
            <LegacyText>No annotations</LegacyText>
          </LegacyBox>
        )}
      </LegacyBox>
    </LegacyBox>
  );
};

export default AnnotationList;
